import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import {
  altColorDarkText,
  altColorLighter,
  borderColor,
  helperTextColor,
  headingFont,
  mainColorDarker,
  mainColorLighter,
  shadow1,
} from 'src/ui/theme/tokens'

export const EditorGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gap: 20,
  alignItems: 'start',
  '& > *': { minWidth: 0 },
  '& .editor-title': {
    padding: 24,
    background: '#fff',
    border: `1px solid ${borderColor}`,
    borderRadius: 24,
    boxShadow: shadow1,
  },
  '& .editor-meta': {
    padding: 20,
    background: mainColorLighter,
    border: `1px solid ${borderColor}`,
    borderRadius: 24,
    boxShadow: shadow1,
  },
  '& .editor-meta > *': {
    background: 'transparent',
    border: 0,
    boxShadow: 'none',
    padding: 0,
    margin: 0,
  },
  '& .editor-title > *': {
    background: 'transparent',
    border: 0,
    boxShadow: 'none',
    padding: 0,
    margin: 0,
  },
  '& .editor-main': { minWidth: 0 },
  '& .editor-main > *': { margin: 0, borderRadius: 24 },
  '& .editor-actions': {
    background: '#fff',
    border: `1px solid ${borderColor}`,
    padding: 20,
    borderRadius: 22,
  },
  '&.editor-create .editor-actions': {
    background: 'transparent',
    border: 0,
    padding: 0,
    borderRadius: 0,
    boxShadow: 'none',
  },
  '& .MuiFormControl-root': { minWidth: 0 },
  '& .MuiInputBase-root': { boxShadow: 'none' },
  '& .editor-meta .MuiAutocomplete-root': {
    marginBottom: 12,
    minWidth: '0 !important',
    width: '100%',
  },
  '& .editor-meta .MuiFormControl-root': {
    minWidth: '0 !important',
    width: '100%',
  },
  '& .editor-meta .MuiFormHelperText-root': { marginLeft: 0 },
  '&.editor-poll .editor-meta': {
    background: altColorLighter,
    borderColor: '#eddccb',
  },
  '&.editor-create .editor-meta': {
    padding: 0,
    background: 'transparent',
    border: 0,
    boxShadow: 'none',
  },
  '&.editor-create .editor-meta > .MuiAccordion-root': {
    padding: 0,
    margin: 0,
    border: `1px solid ${borderColor}`,
    borderRadius: 24,
    boxShadow: shadow1,
  },
  '&.editor-create .editor-main:empty': { display: 'none' },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'minmax(0, 1fr) 260px',
    '& .editor-title, & .editor-format, & .editor-nav, & .editor-notice, & .editor-actions':
      {
        gridColumn: '1 / -1',
      },
    '& .editor-main': { gridColumn: 1, gridRow: 2 },
    '& .editor-meta': { gridColumn: 2, gridRow: 2 },
    '&.editor-with-format .editor-main, &.editor-with-format .editor-meta, &.editor-with-nav .editor-main, &.editor-with-nav .editor-meta':
      { gridRow: 3 },
    '&.editor-owner-settings .editor-main, &.editor-owner-settings .editor-meta':
      { gridRow: 1 },
    '&.editor-create': {
      gridTemplateColumns: 'minmax(0, 1fr)',
      '& > .editor-main, & > .editor-meta': {
        gridColumn: 'auto',
        gridRow: 'auto',
      },
    },
  },
  [theme.breakpoints.down('sm')]: {
    gap: 16,
    '& .editor-title, & .editor-meta': { padding: 16 },
    '&.editor-create .editor-meta': { padding: 0 },
  },
}))

export function EditorSectionHeading({
  step,
  title,
  description,
  poll,
}: {
  step?: string
  title: string
  description?: string
  poll?: boolean
}) {
  return (
    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', mb: 1.5 }}>
      {step && (
        <Box
          aria-hidden
          sx={{
            width: 28,
            height: 30,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            borderRadius: '10px',
            background: poll ? altColorLighter : mainColorLighter,
            color: poll ? altColorDarkText : mainColorDarker,
            fontWeight: 800,
            fontSize: 12,
          }}
        >
          {step}
        </Box>
      )}
      <Box>
        <Typography
          component="h2"
          fontWeight={800}
          fontSize={18}
          textAlign="left"
          sx={{
            fontFamily: headingFont,
            letterSpacing: '-0.015em',
            ...(poll && { color: '#693413' }),
          }}
        >
          {title}
        </Typography>
        {description && (
          <Typography
            color={poll ? '#855c3d' : helperTextColor}
            fontSize={12}
            textAlign="left"
          >
            {description}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export function EditorPageHeading({
  title,
  description,
}: {
  title: string
  description: string
}) {
  return (
    <Box sx={{ mb: 3, textAlign: 'left' }}>
      <Typography
        variant="overline"
        sx={{ color: helperTextColor, letterSpacing: 1.5, fontWeight: 800 }}
      >
        CREATOR STUDIO
      </Typography>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 600,
          fontSize: { xs: 28, sm: 34 },
          lineHeight: 1.15,
          mb: 1,
        }}
      >
        {title}
      </Typography>
      <Typography variant="body2" color={helperTextColor}>
        {description}
      </Typography>
    </Box>
  )
}
